import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Alert,
  IconButton,
  Chip,
  Collapse,
  Divider,
  InputAdornment,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import { ExpandMore, ExpandLess, Search, Clear, Refresh, DeleteForever } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { STORE_TYPES, normalizeStoreTypeValue, formatStoreTypeLabel } from '../../constants/storeTypes';
import ForceDeleteIssuanceDialog from './ForceDeleteIssuanceDialog';
import {
  isQuantityDraftInput,
  parseQuantityFloat,
  unitRowCountForQuantity
} from '../../utils/quantityFloat';
import { PART_UNIT_CONDITIONS, PART_CONDITION_LABELS } from '../../constants/partUnitConditions';
import { getWorkOrderDisplayNumber } from '../../utils/workOrderDisplayNumber';
import { useWorkOrderDisplayContext } from '../maintenance/hooks/useWorkOrderDisplayContext';

function issuanceRecipientLabel(record) {
  if (record?.issuedTo && (record.issuedTo.firstName != null || record.issuedTo.lastName != null)) {
    const n = `${record.issuedTo.firstName || ''} ${record.issuedTo.lastName || ''}`.trim();
    if (n) return n;
  }
  const ext = String(record?.issuedToExternalName || '').trim();
  if (ext) {
    const org = String(record?.issuedToExternalCompany || '').trim();
    return org ? `${ext} (${org})` : ext;
  }
  return 'N/A';
}

function getReturnItemDescription(item) {
  const fromInventory = item?.inventoryItemId?.description;
  if (fromInventory) return String(fromInventory).trim();
  if (item?.description) return String(item.description).trim();
  return '';
}

function getReturnWorkOrderLabel(record, woDisplayContext) {
  if (!record) return 'N/A';
  const wo = typeof record.workOrderId === 'object' ? record.workOrderId : null;
  const number = record.workOrderNumber || getWorkOrderDisplayNumber(wo, woDisplayContext) || '';
  const title = record.workOrderTitle || wo?.title || '';
  if (number && title && number !== title) return `${number} — ${title}`;
  return number || title || 'N/A';
}

const defaultReturnQuantity = (maxReturnable) => {
  const cap = Math.max(0, Number(maxReturnable) || 0);
  if (cap <= 0) return 0;
  if (cap <= 1) return cap;
  return 1;
};

const ReturnsModule = ({ recordId: linkRecordId, recordType }) => {
  const [issuances, setIssuances] = useState([]);
  const [selectedIssuance, setSelectedIssuance] = useState(null);
  const [formData, setFormData] = useState({
    returnReason: ''
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [returnsHistory, setReturnsHistory] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [unitDetailsDialog, setUnitDetailsDialog] = useState({ open: false, inventoryItemId: '', rows: [] });
  const [quantityDrafts, setQuantityDrafts] = useState({});
  const woDisplayContext = useWorkOrderDisplayContext();
  const [forceDeleteDialog, setForceDeleteDialog] = useState({ open: false, issuance: null });
  const { showToast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;
  const historyColSpan = isSuperAdmin ? 9 : 8;

  const fetchReturnsHistory = useCallback(async () => {
    try {
      setLoadingReturns(true);
      // Fetch both RETURNED and PARTIAL_RETURN status issuances
      const [returnedResponse, partialResponse] = await Promise.all([
        axios.get('/api/store/issuances?status=RETURNED'),
        axios.get('/api/store/issuances?status=PARTIAL_RETURN')
      ]);
      const allReturns = [...returnedResponse.data, ...partialResponse.data];
      // Sort by return date descending
      allReturns.sort((a, b) => {
        const dateA = a.returnDate ? new Date(a.returnDate) : new Date(0);
        const dateB = b.returnDate ? new Date(b.returnDate) : new Date(0);
        return dateB - dateA;
      });
      setReturnsHistory(allReturns);
    } catch (error) {
      console.error('Error fetching returns history:', error);
      showToast('Error loading returns history', 'error');
    } finally {
      setLoadingReturns(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchIssuances();
    fetchReturnsHistory();
  }, [fetchReturnsHistory]);

  useEffect(() => {
    if (recordType === 'issuance' && linkRecordId && returnsHistory.length > 0) {
      const exists = returnsHistory.some((r) => String(r._id) === String(linkRecordId));
      if (exists) setExpandedRows((prev) => ({ ...prev, [linkRecordId]: true }));
    }
  }, [recordType, linkRecordId, returnsHistory]);

  const fetchIssuances = async () => {
    try {
      const response = await axios.get('/api/store/issuances', {
        params: { status: 'ISSUED,PARTIAL_RETURN' }
      });
      setIssuances(response.data);
    } catch (error) {
      console.error('Error fetching issuances:', error);
    }
  };

  const lineRemaining = (item) => {
    const issued = item.quantity || 0;
    const returned = item.quantityReturned || 0;
    return Math.max(0, issued - returned);
  };

  const buildDefaultUnitRow = (issuanceLine, unitIndex = 0) => {
    const issuedUnit = Array.isArray(issuanceLine?.unitDetails) ? issuanceLine.unitDetails[unitIndex] : null;
    return {
      condition: issuedUnit?.condition || 'GOOD',
      batchNumber: issuedUnit?.batchNumber || '',
      serialNumber: issuedUnit?.serialNumber || '',
      storeType: normalizeStoreTypeValue(issuedUnit?.storeType || issuanceLine?.storeType || '')
    };
  };

  const buildUnitDetailsForQuantity = (issuanceLine, quantity, existing = []) => {
    const unitRows = unitRowCountForQuantity(quantity);
    return Array.from({ length: unitRows }, (_, idx) => ({
      ...buildDefaultUnitRow(issuanceLine, idx),
      ...(existing[idx] || {})
    }));
  };

  const handleIssuanceSelect = (issuanceId) => {
    const issuance = issuances.find((i) => i._id === issuanceId);
    setSelectedIssuance(issuance);
    setQuantityDrafts({});
    setSelectedItems(
      issuance?.items
        .filter((item) => lineRemaining(item) > 0)
        .map((item) => {
          const id = item.inventoryItemId?._id || item.inventoryItemId;
          const max = lineRemaining(item);
          const qty = defaultReturnQuantity(max);
          return {
            inventoryItemId: id,
            quantity: qty,
            maxReturnable: max,
            unitDetails: buildUnitDetailsForQuantity(item, qty)
          };
        }) || []
    );
  };

  const getReturnQuantityDisplay = (inventoryItemId, selectedRow, fallback) => {
    const idStr = String(inventoryItemId);
    if (quantityDrafts[idStr] !== undefined) return quantityDrafts[idStr];
    if (selectedRow?.quantity != null) return String(selectedRow.quantity);
    return String(fallback);
  };

  const handleReturnQuantityChange = (inventoryItemId, rawValue) => {
    const idStr = String(inventoryItemId);
    if (!isQuantityDraftInput(rawValue)) return;
    setQuantityDrafts((prev) => ({ ...prev, [idStr]: rawValue }));
  };

  const commitReturnQuantity = (inventoryItemId) => {
    const idStr = String(inventoryItemId);
    const draft = quantityDrafts[idStr];
    if (draft === undefined) return;

    const line = selectedIssuance?.items?.find(
      (it) => String(it.inventoryItemId?._id || it.inventoryItemId) === idStr
    );
    const cap = line ? lineRemaining(line) : undefined;

    setSelectedItems((prev) =>
      prev.map((row) => {
        if (String(row.inventoryItemId) !== idStr) return row;
        const maxCap = cap ?? row.maxReturnable ?? row.quantity;
        const n = parseQuantityFloat(draft, {
          min: 0.001,
          max: maxCap,
          fallback: Math.min(maxCap, 0.001)
        });
        const issuanceLine = selectedIssuance?.items?.find(
          (it) => String(it.inventoryItemId?._id || it.inventoryItemId) === idStr
        );
        return {
          ...row,
          quantity: n,
          maxReturnable: maxCap,
          unitDetails: buildUnitDetailsForQuantity(issuanceLine, n, row.unitDetails)
        };
      })
    );

    setQuantityDrafts((prev) => {
      const next = { ...prev };
      delete next[idStr];
      return next;
    });
  };

  const resolveSelectedItems = () => selectedItems.map((row) => {
    const idStr = String(row.inventoryItemId);
    const draft = quantityDrafts[idStr];
    if (draft === undefined) return row;
    const line = selectedIssuance?.items?.find(
      (it) => String(it.inventoryItemId?._id || it.inventoryItemId) === idStr
    );
    const maxCap = line ? lineRemaining(line) : (row.maxReturnable ?? row.quantity);
    const n = parseQuantityFloat(draft, {
      min: 0.001,
      max: maxCap,
      fallback: Math.min(maxCap, 0.001)
    });
    return { ...row, quantity: n, maxReturnable: maxCap };
  });

  const handleItemToggle = (itemId, maxReturnable) => {
    const idStr = String(itemId);
    const index = selectedItems.findIndex((i) => String(i.inventoryItemId) === idStr);
    if (index >= 0) {
      setSelectedItems(selectedItems.filter((_, i) => i !== index));
      setQuantityDrafts((prev) => {
        const next = { ...prev };
        delete next[idStr];
        return next;
      });
    } else {
      const issuanceLine = selectedIssuance?.items?.find(
        (it) => String(it.inventoryItemId?._id || it.inventoryItemId) === idStr
      );
      setSelectedItems([
        ...selectedItems,
        {
          inventoryItemId: itemId,
          quantity: defaultReturnQuantity(maxReturnable),
          maxReturnable,
          unitDetails: buildUnitDetailsForQuantity(issuanceLine, defaultReturnQuantity(maxReturnable))
        }
      ]);
    }
  };

  const openUnitDetailsDialog = (inventoryItemId) => {
    const idStr = String(inventoryItemId);
    const resolvedRow = resolveSelectedItems().find((row) => String(row.inventoryItemId) === idStr);
    if (!resolvedRow) return;
    const issuanceLine = selectedIssuance?.items?.find(
      (it) => String(it.inventoryItemId?._id || it.inventoryItemId) === idStr
    );
    const qty = Math.max(0, Number(resolvedRow.quantity || 0));
    const existing = Array.isArray(resolvedRow.unitDetails) ? resolvedRow.unitDetails : [];
    setUnitDetailsDialog({
      open: true,
      inventoryItemId,
      rows: buildUnitDetailsForQuantity(issuanceLine, qty, existing)
    });
  };

  const saveUnitDetailsDialog = () => {
    if (!unitDetailsDialog.inventoryItemId) return;
    setSelectedItems((prev) => prev.map((row) => (
      String(row.inventoryItemId) === String(unitDetailsDialog.inventoryItemId)
        ? { ...row, unitDetails: unitDetailsDialog.rows }
        : row
    )));
    setUnitDetailsDialog({ open: false, inventoryItemId: '', rows: [] });
  };

  const handleSubmit = async () => {
    if (!selectedIssuance) {
      showToast('Please select an issuance record', 'error');
      return;
    }

    const itemsToReturn = resolveSelectedItems();

    if (itemsToReturn.length === 0) {
      showToast('Please select at least one item to return', 'error');
      return;
    }

    if (!formData.returnReason) {
      showToast('Please provide a return reason', 'error');
      return;
    }

    const missingStore = itemsToReturn.some((row) => {
      const unitRows = unitRowCountForQuantity(row.quantity);
      if (unitRows <= 0) return false;
      const units = Array.isArray(row.unitDetails) ? row.unitDetails.slice(0, unitRows) : [];
      return units.length < unitRows || units.some((unit) => !normalizeStoreTypeValue(unit?.storeType));
    });
    if (missingStore) {
      showToast('Select a store for each unit in Unit Details', 'error');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/store/return', {
        issuanceRecordId: selectedIssuance._id,
        items: itemsToReturn.map(({ inventoryItemId, quantity, unitDetails }) => {
          const rowCount = unitRowCountForQuantity(quantity);
          const normalizedUnits = Array.isArray(unitDetails)
            ? unitDetails.slice(0, rowCount).map((unit) => ({
              condition: unit.condition || 'GOOD',
              batchNumber: unit.batchNumber || '',
              serialNumber: unit.serialNumber || '',
              storeType: normalizeStoreTypeValue(unit.storeType) || undefined
            }))
            : [];
          return {
            inventoryItemId,
            quantity,
            unitDetails: normalizedUnits
          };
        }),
        returnReason: formData.returnReason
      });
      showToast('Items returned successfully', 'success');
      setFormData({ returnReason: '' });
      setSelectedIssuance(null);
      setSelectedItems([]);
      setQuantityDrafts({});
      fetchIssuances();
      fetchReturnsHistory(); // Refresh returns history
    } catch (error) {
      console.error('Error returning items:', error);
      showToast(error.response?.data?.message || 'Error returning items', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleRowExpansion = (issuanceId) => {
    setExpandedRows(prev => ({
      ...prev,
      [issuanceId]: !prev[issuanceId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'RETURNED':
        return 'success';
      case 'PARTIAL_RETURN':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    setDateFromFilter('');
    setDateToFilter('');
  };

  const hasActiveFilters = () => {
    return searchTerm !== '' || 
           statusFilter !== 'ALL' || 
           dateFromFilter !== '' || 
           dateToFilter !== '';
  };

  const filteredReturns = returnsHistory.filter(returnRecord => {
    // Apply status filter
    if (statusFilter !== 'ALL' && returnRecord.status !== statusFilter) {
      return false;
    }

    // Apply date range filter
    if (dateFromFilter && returnRecord.returnDate) {
      const fromDate = new Date(dateFromFilter);
      fromDate.setHours(0, 0, 0, 0);
      const returnDate = new Date(returnRecord.returnDate);
      if (returnDate < fromDate) return false;
    }
    if (dateToFilter && returnRecord.returnDate) {
      const toDate = new Date(dateToFilter);
      toDate.setHours(23, 59, 59, 999);
      const returnDate = new Date(returnRecord.returnDate);
      if (returnDate > toDate) return false;
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const issuedToName = issuanceRecipientLabel(returnRecord).toLowerCase();
      const workOrderLabel = getReturnWorkOrderLabel(returnRecord, woDisplayContext).toLowerCase();
      const partNumbers = returnRecord.items.map(item => item.partNumber?.toLowerCase() || '').join(' ');
      const descriptions = returnRecord.items.map(item => getReturnItemDescription(item).toLowerCase()).join(' ');
      const returnReason = returnRecord.returnReason?.toLowerCase() || '';
      
      if (!issuedToName.includes(term) && 
          !workOrderLabel.includes(term) && 
          !partNumbers.includes(term) &&
          !descriptions.includes(term) &&
          !returnReason.includes(term)) return false;
    }

    return true;
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Returns
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Process returns from maintenance teams. Set the return quantity, then open Unit Details to choose destination store
        (Tools, Quarantine, or Scrape), condition, and serial number for each returned unit.
      </Alert>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Process Return" />
          <Tab label="Returns History" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Select Issuance Record</InputLabel>
              <Select
                value={selectedIssuance?._id || ''}
                label="Select Issuance Record"
                onChange={(e) => handleIssuanceSelect(e.target.value)}
              >
                {issuances.map((issuance) => (
                  <MenuItem key={issuance._id} value={issuance._id}>
                    {getReturnWorkOrderLabel(issuance, woDisplayContext)} —{' '}
                    {issuanceRecipientLabel(issuance)} — {issuance.items.length} item(s) —{' '}
                    {new Date(issuance.issuedDate).toLocaleDateString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {selectedIssuance && (
            <>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Work Order: {getReturnWorkOrderLabel(selectedIssuance, woDisplayContext)}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Items from Issuance:
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Select</TableCell>
                        <TableCell>Part Number</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Issued</TableCell>
                        <TableCell align="right">Already returned</TableCell>
                        <TableCell align="right">Remaining</TableCell>
                        <TableCell align="right">Qty to return</TableCell>
                        <TableCell>Unit Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedIssuance.items.map((item) => {
                        const rowId = item.inventoryItemId?._id || item.inventoryItemId;
                        const isSelected = selectedItems.some(
                          (i) => String(i.inventoryItemId) === String(rowId)
                        );
                        const rem = lineRemaining(item);
                        const returned = item.quantityReturned || 0;
                        const selectedRow = selectedItems.find(
                          (i) => String(i.inventoryItemId) === String(rowId)
                        );
                        return (
                          <TableRow key={String(rowId)}>
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                disabled={rem <= 0}
                                onChange={() => handleItemToggle(rowId, rem)}
                              />
                            </TableCell>
                            <TableCell>{item.partNumber}</TableCell>
                            <TableCell sx={{ minWidth: 180, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {getReturnItemDescription(item) || '—'}
                            </TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">{returned}</TableCell>
                            <TableCell align="right">{rem}</TableCell>
                            <TableCell align="right" sx={{ minWidth: 120 }}>
                              {isSelected && rem > 0 ? (
                                <TextField
                                  type="number"
                                  size="small"
                                  inputProps={{ min: 0.001, max: rem, step: 'any' }}
                                  value={getReturnQuantityDisplay(rowId, selectedRow, rem)}
                                  onChange={(e) => handleReturnQuantityChange(rowId, e.target.value)}
                                  onBlur={() => commitReturnQuantity(rowId)}
                                  sx={{ width: 110 }}
                                />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>
                              {isSelected ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openUnitDetailsDialog(rowId)}
                                >
                                  Units ({selectedRow?.unitDetails?.length || 0})
                                </Button>
                              ) : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Return Reason *"
                  value={formData.returnReason}
                  onChange={(e) => setFormData({ ...formData, returnReason: e.target.value })}
                  required
                  multiline
                  rows={2}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !selectedIssuance || selectedItems.length === 0 || !formData.returnReason}
          >
            {loading ? 'Processing...' : 'Process Return'}
          </Button>
        </Box>
      </Paper>
      )}

      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Returns History
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={fetchReturnsHistory}
              disabled={loadingReturns}
            >
              Refresh
            </Button>
          </Box>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by part number, description, return reason, issued to..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All Status</MenuItem>
                    <MenuItem value="RETURNED">Returned</MenuItem>
                    <MenuItem value="PARTIAL_RETURN">Partial Return</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Return Date From"
                  type="date"
                  value={dateFromFilter}
                  onChange={(e) => setDateFromFilter(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Return Date To"
                  type="date"
                  value={dateToFilter}
                  onChange={(e) => setDateToFilter(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              {hasActiveFilters() && (
                <Grid item xs={12} sm={12} md={12}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<Clear />}
                    onClick={handleClearFilters}
                  >
                    Clear All Filters
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Results Count */}
          {hasActiveFilters() && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredReturns.length} of {returnsHistory.length} return records
              </Typography>
            </Box>
          )}

          {/* Returns History Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="50px"></TableCell>
                  <TableCell>Return Date</TableCell>
                  <TableCell>Issued Date</TableCell>
                  <TableCell>Issued To</TableCell>
                  <TableCell>Work Order</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Return Reason</TableCell>
                  {isSuperAdmin && <TableCell align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingReturns ? (
                  <TableRow>
                    <TableCell colSpan={historyColSpan} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Loading returns history...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredReturns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={historyColSpan} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No return records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReturns.map((returnRecord) => (
                    <React.Fragment key={returnRecord._id}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(returnRecord._id)}
                          >
                            {expandedRows[returnRecord._id] ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          {returnRecord.returnDate 
                            ? new Date(returnRecord.returnDate).toLocaleString()
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {new Date(returnRecord.issuedDate).toLocaleString()}
                        </TableCell>
                        <TableCell>{issuanceRecipientLabel(returnRecord)}</TableCell>
                        <TableCell>
                          {getReturnWorkOrderLabel(returnRecord, woDisplayContext)}
                        </TableCell>
                        <TableCell>
                          {returnRecord.items.length} item(s)
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={returnRecord.status}
                            color={getStatusColor(returnRecord.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {returnRecord.returnReason || 'N/A'}
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell align="center">
                            <Tooltip title="Force Delete (Super Admin) — reverses inventory and permanently deletes">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setForceDeleteDialog({ open: true, issuance: returnRecord })}
                              >
                                <DeleteForever fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={historyColSpan} sx={{ py: 0 }}>
                          <Collapse in={expandedRows[returnRecord._id]} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Returned Items Details:
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Part Number</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell align="right">Issued qty</TableCell>
                                    <TableCell align="right">Returned (total)</TableCell>
                                    <TableCell>Serial Number</TableCell>
                                    <TableCell>Store</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {returnRecord.items.map((item, idx) => {
                                    const returnedUnits = Array.isArray(item.returnedUnitDetails) ? item.returnedUnitDetails : [];
                                    const storeSummary = returnedUnits.length > 0
                                      ? [...new Set(returnedUnits.map((u) => formatStoreTypeLabel(u.storeType)).filter((s) => s && s !== '—'))].join(', ')
                                      : formatStoreTypeLabel(item.storeType);
                                    return (
                                      <TableRow key={idx}>
                                        <TableCell>{item.partNumber}</TableCell>
                                        <TableCell sx={{ minWidth: 180, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                          {getReturnItemDescription(item) || '—'}
                                        </TableCell>
                                        <TableCell align="right">{item.quantity}</TableCell>
                                        <TableCell align="right">{item.quantityReturned || 0}</TableCell>
                                        <TableCell>{item.serialNumber || 'N/A'}</TableCell>
                                        <TableCell>{storeSummary || '—'}</TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                              {returnRecord.items.some((item) => Array.isArray(item.returnedUnitDetails) && item.returnedUnitDetails.length > 0) && (
                                <>
                                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                    Returned unit breakdown:
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Part Number</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Store</TableCell>
                                        <TableCell>Condition</TableCell>
                                        <TableCell>Batch</TableCell>
                                        <TableCell>Serial</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {returnRecord.items.flatMap((item) => (
                                        (Array.isArray(item.returnedUnitDetails) ? item.returnedUnitDetails : []).map((unit, unitIdx) => (
                                          <TableRow key={`${item.partNumber}-${unitIdx}`}>
                                            <TableCell>{item.partNumber}</TableCell>
                                            <TableCell sx={{ minWidth: 180, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                              {getReturnItemDescription(item) || '—'}
                                            </TableCell>
                                            <TableCell>{formatStoreTypeLabel(unit.storeType)}</TableCell>
                                            <TableCell>{unit.condition || '—'}</TableCell>
                                            <TableCell>{unit.batchNumber || '—'}</TableCell>
                                            <TableCell>{unit.serialNumber || '—'}</TableCell>
                                          </TableRow>
                                        ))
                                      ))}
                                    </TableBody>
                                  </Table>
                                </>
                              )}
                              {returnRecord.deliveryNote && (
                                <>
                                  <Divider sx={{ my: 2 }} />
                                  <Typography variant="subtitle2" gutterBottom>
                                    Original Delivery Note:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                    {returnRecord.deliveryNote}
                                  </Typography>
                                </>
                              )}
                              <Divider sx={{ my: 2 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Return Reason:</strong> {returnRecord.returnReason || 'N/A'}
                                </Typography>
                                {returnRecord.returnDueDate && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    <strong>Return Due Date:</strong> {new Date(returnRecord.returnDueDate).toLocaleDateString()}
                                  </Typography>
                                )}
                                {returnRecord.issuedBy && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    <strong>Originally Issued By:</strong> {returnRecord.issuedBy.firstName} {returnRecord.issuedBy.lastName}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Dialog
        open={unitDetailsDialog.open}
        onClose={() => setUnitDetailsDialog({ open: false, inventoryItemId: '', rows: [] })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Return Unit Details</DialogTitle>
        <DialogContent dividers>
          {unitDetailsDialog.rows.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Set a return quantity greater than zero before editing unit details.
            </Alert>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Unit #</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Store *</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Condition</TableCell>
                    <TableCell>Batch Number</TableCell>
                    <TableCell>Serial Number</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unitDetailsDialog.rows.map((row, idx) => (
                    <TableRow key={`return-unit-${idx}`}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth required>
                          <InputLabel id={`return-store-label-${idx}`}>Store</InputLabel>
                          <Select
                            labelId={`return-store-label-${idx}`}
                            label="Store"
                            value={normalizeStoreTypeValue(row.storeType)}
                            onChange={(e) => setUnitDetailsDialog((prev) => ({
                              ...prev,
                              rows: prev.rows.map((x, i) => (
                                i === idx ? { ...x, storeType: e.target.value } : x
                              ))
                            }))}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) return 'Select store';
                              const opt = STORE_TYPES.find((t) => t.value === selected);
                              return opt?.label || selected;
                            }}
                          >
                            <MenuItem value="">
                              <em>Select store</em>
                            </MenuItem>
                            {STORE_TYPES.map((t) => (
                              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <InputLabel id={`return-condition-label-${idx}`}>Condition</InputLabel>
                          <Select
                            labelId={`return-condition-label-${idx}`}
                            label="Condition"
                            value={row.condition || 'GOOD'}
                            onChange={(e) => setUnitDetailsDialog((prev) => ({
                              ...prev,
                              rows: prev.rows.map((x, i) => (
                                i === idx ? { ...x, condition: e.target.value } : x
                              ))
                            }))}
                          >
                            {PART_UNIT_CONDITIONS.map((v) => (
                              <MenuItem key={v} value={v}>{PART_CONDITION_LABELS[v] || v}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.batchNumber || ''}
                          onChange={(e) => setUnitDetailsDialog((prev) => ({
                            ...prev,
                            rows: prev.rows.map((x, i) => (i === idx ? { ...x, batchNumber: e.target.value } : x))
                          }))}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.serialNumber || ''}
                          onChange={(e) => setUnitDetailsDialog((prev) => ({
                            ...prev,
                            rows: prev.rows.map((x, i) => (i === idx ? { ...x, serialNumber: e.target.value } : x))
                          }))}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnitDetailsDialog({ open: false, inventoryItemId: '', rows: [] })}>Cancel</Button>
          <Button onClick={saveUnitDetailsDialog} variant="contained" disabled={unitDetailsDialog.rows.length === 0}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ForceDeleteIssuanceDialog
        open={forceDeleteDialog.open}
        issuance={forceDeleteDialog.issuance}
        onClose={() => setForceDeleteDialog({ open: false, issuance: null })}
        onDeleted={fetchReturnsHistory}
      />
    </Box>
  );
};

export default ReturnsModule;

